/* eslint-disable max-lines */

import { Dir } from '@angular/cdk/bidi';
import {
  OriginConnectionPosition,
  Overlay,
  OverlayConnectionPosition,
  OverlayRef,
  OverlayConfig,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import {
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

import { BehaviorSubject, merge, of, pipe, Subject, Subscription } from 'rxjs';
import { delay, filter, first, map, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { filterBoolean, setInputs, TaskPriority, TaskScheduler } from '@recruitee/common';
import { asViewRef } from '@recruitee/utils';

import { RtPositionBuilderService } from '../../core/overlay/position/position-builder.service';
import { RtFlexibleConnectedPositionStrategy } from '../../core/overlay/position/rt-flexible-connected-position';
import { OVERLAY_LEVEL } from '../../dialog/providers';
import { RtTooltipInvalidPositionError } from './tooltip-errors';
import { TooltipPosition, TooltipType, TooltipVisibility } from './tooltip-inner.component';
import { TooltipComponent, TooltipComponentInputKeys, TooltipInputs } from './tooltip.component';

/** Time in ms to delay before changing the tooltip visibility to hidden */
export const TOUCHEND_HIDE_DELAY = 1500;
const TOOLTIP_OFFSET = 6;

/**
 * Directive that attaches a material design tooltip to the host element. Animates the showing and
 * hiding of a tooltip provided position (defaults to below the element).
 *
 * https://material.google.com/components/tooltips.html
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[rtTooltip]',
  host: {
    '(longpress)': 'showSubject.next()',
    '(touchend)': 'onTouchEnd()',
    '(mouseenter)': 'showSubject.next()',
    '(mouseleave)': 'onMouseLeave()',
  },
  exportAs: 'rtTooltip',
  standalone: true,
})

// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class RtTooltip implements OnChanges, OnDestroy {
  /** The default delay in ms before showing the tooltip after show is called */
  @Input('rtTooltipShowDelay') public showDelay: number = 300;
  /** The default delay in ms before hiding the tooltip after hide is called */
  @Input('rtTooltipHideDelay') public hideDelay: number = 300;

  @Input('rtTooltipType') public type: TooltipType = 'default';
  @Input('rtTooltipMaxWidth') public maxWidth: number | null | undefined = 300;
  @Input('rtTooltipWordWrap') public wordWrap: boolean | 'pre-line' = true;
  @Input('rtTooltipHasBorder') public hasBorder: boolean = false;
  @Input('rtTooltipHasShadow') public hasShadow: boolean = false;
  @Input('rtTooltipHasNoArrow') public hasNoArrow: boolean = false;
  @Input('rtTooltipForceShow') public forceShow: boolean = false;
  @Input('rtTooltipHasTextAlignCenter') public hasTextAlignCenter: boolean = false;
  @Input('rtTooltip') public message?: string | null | false;
  @Input('rtTooltipTemplate') public templateRef: TemplateRef<null> | null = null;
  /** Allows the user to define the position of the tooltip relative to the parent element */
  @Input('rtTooltipPosition')
  public position: TooltipPosition = 'above';
  /**
   * By default arrow is always in the middle of tooltip, if origin element is close to
   * the edge of the screen this will push the arrow to point to the origin element
   */
  @Input('rtTooltipArrowPush') public withArrowPush: boolean = false;

  @Input('rtTooltipEnabled')
  public tooltipEnabled: boolean | (() => boolean) = true;

  @Input('rtTooltipSkipTransition') public skipTransition: boolean = false;

  public showSubject = new Subject<{ delay: number } | void>();
  public hideSubject = new BehaviorSubject({ eventEmitted: false, delay: this.hideDelay });

  private ngOnDestroy$ = new Subject<void>();
  private raftick: boolean = false;
  private show$ = this.showSubject.asObservable();
  private hideEvent$ = this.hideSubject.pipe(filter(event => event.eventEmitted));
  private hideEventDelayed$ = this.hideEvent$.pipe(
    switchMap(event => of(event).pipe(delay(event.delay))),
  );
  private tooltipProps = new BehaviorSubject<Partial<TooltipInputs>>({
    templateRef: null,
    message: '',
    skipTransition: false,
  });
  private tooltipVisibility$ = new BehaviorSubject<TooltipVisibility | null>(null);
  private overlayLevel = inject(OVERLAY_LEVEL);
  private teardownFn?: () => void;

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private scrollDispatcher: ScrollDispatcher,
    private cdRef: ChangeDetectorRef,
    private taskScheduler: TaskScheduler,
    private rtOverlayPositionBuilder: RtPositionBuilderService,
    @Optional() private dir: Dir,
  ) {}

  public ngOnInit(): void {
    this.taskScheduler.scheduleMacrotask(TaskPriority.Low, () => {
      if (asViewRef(this.cdRef).destroyed) return;

      this.watchTooltipEvents();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const tooltipEnabled = this.isTooltipEnabled();

    if (changes.tooltipEnabled && !tooltipEnabled) {
      this.hideSubject.next({ ...this.hideSubject.value, eventEmitted: true });
    }

    if (changes.tooltipEnabled && this.forceShow && tooltipEnabled) {
      this.showSubject.next();
    }
    const tooltipPropsChanges = TooltipComponentInputKeys.reduce(
      (acc: Partial<TooltipInputs>, key: keyof TooltipInputs) => {
        if (key in changes) {
          return {
            ...acc,
            [key]: changes[key].currentValue,
          };
        }

        return acc;
      },
      {},
    );
    this.tooltipProps.next({
      ...this.tooltipProps.value,
      ...tooltipPropsChanges,
    });
  }

  /**
   * Dispose the tooltip when destroyed.
   */
  public ngOnDestroy(): void {
    this.hideSubject.next({ ...this.hideSubject.value, eventEmitted: true });
    this.teardownFn?.();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  // eslint-disable-next-line max-lines-per-function
  public watchTooltipEvents(): void {
    const resetHideEvent = () =>
      pipe(
        tap(() =>
          this.hideSubject.next({
            ...this.hideSubject.value,
            eventEmitted: false,
          }),
        ),
      );

    this.show$
      .pipe(
        resetHideEvent(),
        switchMap(() => of(null).pipe(delay(this.showDelay), takeUntil(this.hideEvent$))),
        filter(() => {
          const tooltipEnabled = this.isTooltipEnabled();
          const hasMessage = this.message && this.message.trim();
          return Boolean(tooltipEnabled && ((hasMessage && !this.templateRef) || this.templateRef));
        }),
        map(() => this.createTooltip()),
        tap(({ tooltipRef, overlayRef }) => {
          this.updateTooltipProps(tooltipRef, this.tooltipProps.value);
          this.stabilizeOverlayPosition(overlayRef);
          tooltipRef.instance.show();
          this.watchPropsEmptyMessage();
        }),
        switchMap(({ tooltipRef, overlayRef }) => {
          const tooltipInstance = tooltipRef.instance;
          const scrollSubscription = this.attachScroll(overlayRef);
          this.teardownFn = () => {
            scrollSubscription.unsubscribe();
            this.disposeTooltip(overlayRef);
            this.teardownFn = undefined;
          };
          this.pushArrow(overlayRef);

          return merge(
            this.publishVisibilityState$(tooltipInstance),
            this.updateTooltipProps$(tooltipRef),
            this.watchHideEvents$(tooltipInstance).pipe(resetHideEvent()),
          );
        }),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe();
  }

  public watchTooltipPosition(overlayRef: OverlayRef, tooltipRef: ComponentRef<TooltipComponent>) {
    const positionStrategy = <RtFlexibleConnectedPositionStrategy>(
      overlayRef.getConfig().positionStrategy!
    );
    tooltipRef.instance.setTooltipPosition(this.position);

    const setTooltipPosition = (position: TooltipPosition) => {
      tooltipRef.instance.setTooltipPosition(position);
      tooltipRef.changeDetectorRef.detectChanges();
    };
    const growSub = positionStrategy.positionChanges.pipe(take(1)).subscribe(() => {
      positionStrategy.withGrowAfterOpen();
      positionStrategy.apply();
    });
    const positionSub = positionStrategy.positionChanges.subscribe(({ connectionPair }) => {
      if (this.position === 'above' && connectionPair.overlayY === 'top') {
        setTooltipPosition('below');
      } else if (this.position === 'below' && connectionPair.overlayY === 'bottom') {
        setTooltipPosition('above');
      } else if (this.position === 'left' && connectionPair.overlayX === 'start') {
        setTooltipPosition('right');
      } else if (this.position === 'right' && connectionPair.overlayX === 'end') {
        setTooltipPosition('left');
      }
    });
    tooltipRef.onDestroy(() => {
      growSub.unsubscribe();
      positionSub.unsubscribe();
    });
  }

  public pushArrow(overlayRef: OverlayRef): void {
    if (!this.withArrowPush || !overlayRef.hasAttached()) return;

    const overlayRect = overlayRef?.overlayElement.getBoundingClientRect();
    const originRect = this.elementRef.nativeElement.getBoundingClientRect();
    if (this.position === 'above' || this.position === 'below') {
      const arrowExpectedX = originRect.width / 2 + originRect.left;
      const arrowX = overlayRect.width / 2 + overlayRect.left;
      const arrowDiff = arrowExpectedX - arrowX;
      this.tooltipProps.next({
        ...this.tooltipProps.value,
        arrowOffset: { offsetX: arrowDiff, offsetY: 0 },
      });
    } else if (this.position === 'right' || this.position === 'left') {
      const arrowExpectedY = originRect.height / 2 + originRect.top;
      const arrowY = overlayRect.height / 2 + overlayRect.top;
      const arrowDiff = arrowExpectedY - arrowY;
      this.tooltipProps.next({
        ...this.tooltipProps.value,
        arrowOffset: { offsetX: 0, offsetY: arrowDiff },
      });
    }
  }

  public onMouseLeave(): void {
    this.hideSubject.next({ delay: this.hideDelay, eventEmitted: true });
  }

  public onTouchEnd(): void {
    this.hideSubject.next({ delay: TOUCHEND_HIDE_DELAY, eventEmitted: true });
  }

  private updateTooltipProps$(tooltipRef: ComponentRef<TooltipComponent>) {
    return this.tooltipProps.pipe(
      tap(currentState => this.updateTooltipProps(tooltipRef, currentState)),
      takeUntil(this.hideEvent$),
    );
  }

  private publishVisibilityState$(tooltipInstance: TooltipComponent) {
    return tooltipInstance.visibilityChange.pipe(
      tap(visibility => this.tooltipVisibility$.next(visibility)),
      takeUntil(this.hideEvent$),
    );
  }

  private animationEvent$(tooltipInstance: TooltipComponent, name: TooltipVisibility) {
    return tooltipInstance.animationState.pipe(
      filterBoolean,
      filter(animationState => animationState.toState === name),
    );
  }

  private watchHideEvents$(tooltipInstance: TooltipComponent) {
    return this.animationEvent$(tooltipInstance, 'visible').pipe(
      switchMap(() =>
        merge(
          this.hideEventDelayed$,
          tooltipInstance.visibilityChange.pipe(filter(visibility => visibility === 'hidden')),
        ).pipe(first()),
      ),
      tap(() => this.hide(tooltipInstance)),
      switchMap(() => this.animationEvent$(tooltipInstance, 'hidden')),
      tap(() => this.teardownFn?.()),
    );
  }

  private watchPropsEmptyMessage(): void {
    this.tooltipProps
      .pipe(
        map(({ templateRef, message }) => !(templateRef || message)),
        filterBoolean,
        skip(1),
        takeUntil(merge(this.hideEvent$, this.ngOnDestroy$)),
      )
      .subscribe(() => {
        this.hideSubject.next({ delay: 0, eventEmitted: true });
      });
  }

  private attachScroll(overlayRef: OverlayRef): Subscription {
    return this.scrollDispatcher.scrolled(0).subscribe(() => {
      if (!this.raftick) {
        this.raftick = true;
        requestAnimationFrame(() => {
          if (overlayRef) {
            overlayRef.updatePosition();
            this.pushArrow(overlayRef);
          }
          this.raftick = false;
        });
      }
    });
  }

  /** Shows the tooltip after the delay in ms, defaults to tooltip-delay-show or 0ms if no input */
  private updateTooltipProps(
    tooltipRef: ComponentRef<TooltipComponent>,
    currentState: Partial<TooltipInputs>,
  ): void {
    setInputs(tooltipRef, currentState);
    tooltipRef.changeDetectorRef.detectChanges();
  }

  /** Hides the tooltip after the delay in ms, defaults to tooltip-delay-hide or 0ms if no input */
  private hide(tooltipInstance: TooltipComponent): void {
    tooltipInstance.hide();
    this.cdRef.detectChanges();
  }

  /** Shows/hides the tooltip */
  private toggle(): void {
    this.isTooltipVisible()
      ? this.hideSubject.next({ ...this.hideSubject.value, eventEmitted: true })
      : this.showSubject.next();
  }

  /** Returns true if the tooltip is currently visible to the user */
  private isTooltipVisible(): boolean {
    return this.tooltipVisibility$.value === 'visible';
  }

  private stabilizeOverlayPosition(overlayRef: OverlayRef): void {
    const MAX_ATTEMPTS = 5;
    const getOverlayPosition = () => ({
      top: overlayRef.overlayElement.style.top,
      left: overlayRef.overlayElement.style.left,
    });
    let position = getOverlayPosition();
    let isPositionStable = false;
    let updateCount = 0;
    while (!isPositionStable && updateCount < MAX_ATTEMPTS) {
      overlayRef.updatePosition();
      const currentPosition = getOverlayPosition();
      updateCount++;
      isPositionStable =
        currentPosition.left === position.left && currentPosition.top === position.top;
      position = currentPosition;
    }
  }

  private isTooltipEnabled(): boolean {
    return (
      this.tooltipEnabled === null ||
      this.tooltipEnabled === undefined ||
      (typeof this.tooltipEnabled === 'boolean' && this.tooltipEnabled) ||
      (typeof this.tooltipEnabled === 'function' && this.tooltipEnabled())
    );
  }

  /** Create the tooltip to display */
  private createTooltip(): { tooltipRef: ComponentRef<TooltipComponent>; overlayRef: OverlayRef } {
    this.teardownFn?.();
    const overlayRef = this.createOverlay();
    const portal = new ComponentPortal(TooltipComponent, this.viewContainerRef);
    const tooltipRef = overlayRef.attach(portal);
    tooltipRef.changeDetectorRef.detectChanges();
    this.watchTooltipPosition(overlayRef, tooltipRef);

    return { tooltipRef, overlayRef };
  }

  /** Create the overlay config and position strategy */
  private createOverlay(): OverlayRef {
    const { main: originMain, fallback: originFallback } = this.getOrigin();
    const { main: overlayMain, fallback: overlayFallback } = this.getOverlayPosition();
    const main = { ...originMain, ...overlayMain, ...this.getOffset(originMain) };
    const fallback =
      !!originFallback && !!overlayFallback
        ? [{ ...originFallback, ...overlayFallback, ...this.getOffset(originFallback) }]
        : [];
    const strategy = this.rtOverlayPositionBuilder
      .rtFlexibleConnectedTo(this.elementRef)
      .withPositions([main, ...fallback])
      .withOverlayLevel(this.overlayLevel);
    const config = new OverlayConfig();
    config.positionStrategy = strategy;

    return this.overlay.create(config);
  }

  /** Disposes the current tooltip and the overlay it is attached to */
  private disposeTooltip(overlayRef: OverlayRef): void {
    overlayRef.dispose();
  }

  /** Returns the origin position based on the user's position preference */
  private getOrigin(): {
    main: OriginConnectionPosition;
    fallback?: OriginConnectionPosition;
  } {
    if (this.position === 'above' || this.position === 'below') {
      return {
        main: { originX: 'center', originY: this.position === 'above' ? 'top' : 'bottom' },
        fallback: { originX: 'center', originY: this.position === 'above' ? 'bottom' : 'top' },
      };
    } else if (this.position === 'aboveright') {
      return { main: { originX: 'end', originY: 'top' } };
    } else if (this.position === 'righttop') {
      return { main: { originX: 'end', originY: 'top' } };
    } else if (this.position === 'leftbelow') {
      return { main: { originX: 'start', originY: 'bottom' } };
    }

    const isDirectionLtr = !this.dir || this.dir.value === 'ltr';
    if (
      this.position === 'left' ||
      (this.position === 'before' && isDirectionLtr) ||
      (this.position === 'after' && !isDirectionLtr)
    ) {
      return {
        main: { originX: 'start', originY: 'center' },
        fallback: { originX: 'end', originY: 'center' },
      };
    } else if (
      this.position === 'right' ||
      (this.position === 'after' && isDirectionLtr) ||
      (this.position === 'before' && !isDirectionLtr)
    ) {
      return {
        main: { originX: 'end', originY: 'center' },
        fallback: { originX: 'start', originY: 'center' },
      };
    }
    throw new RtTooltipInvalidPositionError(this.position);
  }

  /** Returns the overlay position based on the user's preference */
  // eslint-disable-next-line max-lines-per-function
  private getOverlayPosition(): {
    main: OverlayConnectionPosition;
    fallback?: OverlayConnectionPosition;
  } {
    if (this.position === 'above') {
      return {
        main: { overlayX: 'center', overlayY: 'bottom' },
        fallback: { overlayX: 'center', overlayY: 'top' },
      };
    } else if (this.position === 'below') {
      return {
        main: { overlayX: 'center', overlayY: 'top' },
        fallback: { overlayX: 'center', overlayY: 'bottom' },
      };
    } else if (this.position === 'aboveright') {
      return { main: { overlayX: 'end', overlayY: 'bottom' } };
    } else if (this.position === 'righttop') {
      return { main: { overlayX: 'start', overlayY: 'top' } };
    } else if (this.position === 'leftbelow') {
      return { main: { overlayX: 'start', overlayY: 'top' } };
    }

    const isLtr = !this.dir || this.dir.value === 'ltr';
    if (
      this.position === 'left' ||
      (this.position === 'before' && isLtr) ||
      (this.position === 'after' && !isLtr)
    ) {
      return {
        main: { overlayX: 'end', overlayY: 'center' },
        fallback: { overlayX: 'start', overlayY: 'center' },
      };
    } else if (
      this.position === 'right' ||
      (this.position === 'after' && isLtr) ||
      (this.position === 'before' && !isLtr)
    ) {
      return {
        main: { overlayX: 'start', overlayY: 'center' },
        fallback: { overlayX: 'end', overlayY: 'center' },
      };
    }

    throw new RtTooltipInvalidPositionError(this.position);
  }

  private getOffset(position: OriginConnectionPosition): { offsetX: number; offsetY: number } {
    const offset = TOOLTIP_OFFSET;
    const isLtr = !this.dir || this.dir.value === 'ltr';

    if (position.originY === 'top') {
      return { offsetY: -offset, offsetX: 0 };
    } else if (position.originY === 'bottom') {
      return { offsetY: offset, offsetX: 0 };
    } else if (position.originX === 'start') {
      return { offsetX: isLtr ? -offset : offset, offsetY: 0 };
    } else if (position.originX === 'end') {
      return { offsetX: isLtr ? offset : -offset, offsetY: 0 };
    } else {
      return { offsetX: 0, offsetY: 0 };
    }
  }
}
